import React, { useState } from 'react';
import { Container, Row, Col, Form, Table, Badge, Modal, Nav, ProgressBar, Card } from 'react-bootstrap';
import { useTheme } from '../../context/ThemeContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';

// Aday veri tipi tanımı
interface Lead {
  id: number;
  name: string;
  company: string;
  email: string;
  phone: string;
  status: string;
  source: string;
  lastContact: string;
  score: number;
}

// Kampanya veri tipi tanımı
interface Campaign {
  id: number;
  name: string;
  status: string;
  leads: number;
  conversion: number;
  startDate: string;
  endDate: string;
}

// Örnek aday verileri
const dummyLeads: Lead[] = [
  {
    id: 1,
    name: 'John Smith',
    company: 'Tech Solutions Inc.',
    email: 'john.smith@techsolutions.com',
    phone: '+1 234-567-8901',
    status: 'New',
    source: 'Website',
    lastContact: '2024-03-15',
    score: 85,
  },
  {
    id: 2,
    name: 'Sarah Johnson',
    company: 'Global Industries',
    email: 'sarah.j@globalind.com',
    phone: '+1 234-567-8902',
    status: 'Contacted',
    source: 'LinkedIn',
    lastContact: '2024-03-14',
    score: 92,
  },
  {
    id: 3,
    name: 'Michael Brown',
    company: 'Innovation Labs',
    email: 'm.brown@innovationlabs.com',
    phone: '+1 234-567-8903',
    status: 'Qualified',
    source: 'Email Campaign',
    lastContact: '2024-03-13',
    score: 78,
  },
];

// Örnek kampanya verileri
const dummyCampaigns: Campaign[] = [
  {
    id: 1,
    name: 'Q1 Tech Industry Outreach',
    status: 'Active',
    leads: 150,
    conversion: 35,
    startDate: '2024-01-01',
    endDate: '2024-03-31',
  },
  {
    id: 2,
    name: 'LinkedIn Connection Drive',
    status: 'Active',
    leads: 75,
    conversion: 42,
    startDate: '2024-02-15',
    endDate: '2024-04-15',
  },
];

// İstatistik verileri
const statistics = {
  totalLeads: 1250,
  activeCampaigns: 5,
  conversionRate: 28.5,
  qualifiedLeads: 350,
};

const LeadGenerationPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('leads');
  const [showNewLeadModal, setShowNewLeadModal] = useState<boolean>(false);
  const [showNewCampaignModal, setShowNewCampaignModal] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Durum rengini belirleyen yardımcı fonksiyon
  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'new':
        return 'info';
      case 'contacted':
        return 'warning';
      case 'qualified':
        return 'success';
      default:
        return 'secondary';
    }
  };

  // Arama sonuçlarına göre filtrelenmiş adaylar
  const filteredLeads = dummyLeads.filter(lead =>
    lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lead.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lead.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Container fluid className="marketing-dashboard lead-generation">
      {/* Başlık Bölümü */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-info">
            <div className="header-icon">
              <i className="bi bi-people-fill"></i>
            </div>
            <div className="header-text">
              <h1 className="page-title">Lead Generation</h1>
              <p className="page-subtitle">
                Track and manage your leads, campaigns, and conversion metrics
              </p>
            </div>
          </div>
          <div className="header-actions">
            <Button
              variant="primary"
              leftIcon={<i className="bi bi-plus-lg"></i>}
              onClick={() => setShowNewCampaignModal(true)}
              style={{ color: '#fff' }}
            >
              New Campaign
            </Button>
            <Button
              variant="outline"
              leftIcon={<i className="bi bi-plus-lg"></i>}
              onClick={() => setShowNewLeadModal(true)}
              style={{ color: '#521DB3', borderColor: '#521DB3' }}
            >
              Add Lead
            </Button>
          </div>
        </div>
      </div>

      {/* İstatistik Kartları */}
      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} md={3}>
          <Card className="stat-card content-card">
            <Card.Body className="card-body-custom">
              <div className="d-flex align-items-center mb-3">
                <div className="stat-icon primary">
                  <i className="bi bi-people-fill"></i>
                </div>
                <h6 className="text-muted mb-0">Total Leads</h6>
              </div>
              <h3 className="stat-value">{statistics.totalLeads}</h3>
              <ProgressBar now={75} variant="primary" style={{ height: '6px', borderRadius: '3px' }} />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={3}>
          <Card className="stat-card content-card">
            <Card.Body className="card-body-custom">
              <div className="d-flex align-items-center mb-3">
                <div className="stat-icon success">
                  <i className="bi bi-megaphone-fill"></i>
                </div>
                <h6 className="text-muted mb-0">Active Campaigns</h6>
              </div>
              <h3 className="stat-value">{statistics.activeCampaigns}</h3>
              <ProgressBar now={60} variant="success" style={{ height: '6px', borderRadius: '3px' }} />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={3}>
          <Card className="stat-card content-card">
            <Card.Body className="card-body-custom">
              <div className="d-flex align-items-center mb-3">
                <div className="stat-icon warning">
                  <i className="bi bi-graph-up-arrow"></i>
                </div>
                <h6 className="text-muted mb-0">Conversion Rate</h6>
              </div>
              <h3 className="stat-value">{statistics.conversionRate}%</h3>
              <ProgressBar now={statistics.conversionRate} variant="warning" style={{ height: '6px', borderRadius: '3px' }} />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={3}>
          <Card className="stat-card content-card">
            <Card.Body className="card-body-custom">
              <div className="d-flex align-items-center mb-3">
                <div className="stat-icon info">
                  <i className="bi bi-building"></i>
                </div>
                <h6 className="text-muted mb-0">Qualified Leads</h6>
              </div>
              <h3 className="stat-value">{statistics.qualifiedLeads}</h3>
              <ProgressBar now={45} variant="info" style={{ height: '6px', borderRadius: '3px' }} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Sekmeler */}
      <Nav variant="tabs" className="nav-tabs mb-4">
        <Nav.Item>
          <Nav.Link
            active={activeTab === 'leads'}
            onClick={() => setActiveTab('leads')}
          >
            Leads
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={activeTab === 'campaigns'}
            onClick={() => setActiveTab('campaigns')}
          >
            Campaigns
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={activeTab === 'analytics'}
            onClick={() => setActiveTab('analytics')}
          >
            Analytics
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* Adaylar Tablosu */}
      {activeTab === 'leads' && (
        <Card className="content-card">
          <Card.Body className="card-body-custom">
            <div className="d-flex justify-content-between mb-4">
              <div style={{ maxWidth: '300px', width: '100%' }}>
                <Input
                  placeholder="Search leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftIcon={<i className="bi bi-search"></i>}
                />
              </div>
              <Button variant="outline" leftIcon={<i className="bi bi-funnel"></i>} style={{ color: '#521DB3', borderColor: '#521DB3' }}>
                Filter
              </Button>
            </div>

            <div className="table-responsive">
              <Table hover className="leads-table mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Contact</th>
                    <th>Status</th>
                    <th>Source</th>
                    <th>Last Contact</th>
                    <th>Score</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map(lead => (
                    <tr key={lead.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="avatar-circle">
                            {lead.name.charAt(0)}
                          </div>
                          <span className="fw-medium">{lead.name}</span>
                        </div>
                      </td>
                      <td>{lead.company}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button variant="primary" size="sm" iconOnly className="rounded-circle" style={{ color: '#fff' }}>
                            <i className="bi bi-envelope-fill"></i>
                          </Button>
                          <Button variant="primary" size="sm" iconOnly className="rounded-circle" style={{ color: '#fff' }}>
                            <i className="bi bi-telephone-fill"></i>
                          </Button>
                        </div>
                      </td>
                      <td>
                        <Badge bg={getStatusColor(lead.status)} pill>
                          {lead.status}
                        </Badge>
                      </td>
                      <td>{lead.source}</td>
                      <td>{lead.lastContact}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="me-2">{lead.score}</span>
                          <ProgressBar
                            now={lead.score}
                            style={{ width: '50px', height: '6px', borderRadius: '3px' }}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button variant="ghost" size="sm" iconOnly style={{ color: 'var(--text-secondary)' }}>
                            <i className="bi bi-pencil-fill"></i>
                          </Button>
                          <Button variant="danger" size="sm" iconOnly style={{ color: '#fff' }}>
                            <i className="bi bi-trash-fill"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Kampanyalar Tablosu */}
      {activeTab === 'campaigns' && (
        <Card className="content-card">
          <Card.Body className="card-body-custom">
            <div className="table-responsive">
              <Table hover className="campaigns-table mb-0">
                <thead>
                  <tr>
                    <th>Campaign Name</th>
                    <th>Status</th>
                    <th>Leads</th>
                    <th>Conversion</th>
                    <th>Duration</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dummyCampaigns.map(campaign => (
                    <tr key={campaign.id}>
                      <td className="fw-medium">{campaign.name}</td>
                      <td>
                        <Badge bg="success" pill>
                          {campaign.status}
                        </Badge>
                      </td>
                      <td>{campaign.leads}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="me-2">{campaign.conversion}%</span>
                          <ProgressBar
                            now={campaign.conversion}
                            style={{ width: '50px', height: '6px', borderRadius: '3px' }}
                          />
                        </div>
                      </td>
                      <td>
                        {campaign.startDate} - {campaign.endDate}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button variant="ghost" size="sm" iconOnly style={{ color: 'var(--text-secondary)' }}>
                            <i className="bi bi-pencil-fill"></i>
                          </Button>
                          <Button variant="danger" size="sm" iconOnly style={{ color: '#fff' }}>
                            <i className="bi bi-trash-fill"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Analitik Sekmesi */}
      {activeTab === 'analytics' && (
        <Card className="content-card">
          <Card.Body className="card-body-custom text-center py-5">
            <i className="bi bi-bar-chart-line-fill text-primary" style={{ fontSize: '48px' }}></i>
            <h4 className="mt-3" style={{ color: "var(--text-primary)" }}>Analytics Dashboard</h4>
            <p className="text-muted">Analytics features will be available soon.</p>
          </Card.Body>
        </Card>
      )}

      {/* Yeni Aday Ekleme Modal */}
      <Modal
        show={showNewLeadModal}
        onHide={() => setShowNewLeadModal(false)}
        centered
        contentClassName="bg-transparent border-0"
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">Add New Lead</h5>
            <button
              type="button"
              className="btn-close"
              style={{ filter: isDark ? 'invert(1)' : 'none' }}
              onClick={() => setShowNewLeadModal(false)}
            ></button>
          </div>
          <div className="modal-body p-4">
            <Form>
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Input type="text" />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group>
                    <Form.Label>Company</Form.Label>
                    <Input type="text" />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Input type="email" />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group>
                    <Form.Label>Phone</Form.Label>
                    <Input type="tel" />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Source</Form.Label>
                    <Form.Select className="form-select" defaultValue="website">
                      <option value="website">Website</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="email">Email Campaign</option>
                      <option value="referral">Referral</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </div>
          <div className="modal-footer">
            <Button variant="ghost" onClick={() => setShowNewLeadModal(false)} style={{ color: 'var(--text-secondary)' }}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowNewLeadModal(false)}
              style={{ color: '#fff' }}
            >
              Add Lead
            </Button>
          </div>
        </div>
      </Modal>

      {/* Yeni Kampanya Oluşturma Modal */}
      <Modal
        show={showNewCampaignModal}
        onHide={() => setShowNewCampaignModal(false)}
        centered
        contentClassName="bg-transparent border-0"
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">Create New Campaign</h5>
            <button
              type="button"
              className="btn-close"
              style={{ filter: isDark ? 'invert(1)' : 'none' }}
              onClick={() => setShowNewCampaignModal(false)}
            ></button>
          </div>
          <div className="modal-body p-4">
            <Form>
              <Row className="g-3">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Campaign Name</Form.Label>
                    <Input type="text" />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Input type="date" />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Input type="date" />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Target Industry</Form.Label>
                    <Form.Select className="form-select" defaultValue="tech">
                      <option value="tech">Technology</option>
                      <option value="finance">Finance</option>
                      <option value="healthcare">Healthcare</option>
                      <option value="retail">Retail</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </div>
          <div className="modal-footer">
            <Button variant="ghost" onClick={() => setShowNewCampaignModal(false)} style={{ color: 'var(--text-secondary)' }}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowNewCampaignModal(false)}
              style={{ color: '#fff' }}
            >
              Create Campaign
            </Button>
          </div>
        </div>
      </Modal>
    </Container>
  );
};

export default LeadGenerationPage;
